// ---------------------------------------------------------------------------
// V14 — THE KEYBOARD, AS A PURE FUNCTION.
//
// 12-… §V14.4: "A scheduler filling a stadium does not reach for a mouse. Full
// keyboard operation is a definition-of-done item, not an enhancement."
//
// AND THE REASON THIS IS A FILE RATHER THAN AN onKeyDown IN THE COMPONENT is
// stated in the same section, as a harness note:
//
//   "The browser harness reports an EMPTY e.key for synthesised key events, so
//   keyboard behaviour cannot be verified through it. Test the handlers
//   directly as units, and state in the ledger that end-to-end keyboard
//   verification is a manual check. Do not report keyboard support as verified
//   on the strength of a harness that cannot observe it."
//
// A handler buried in JSX is a handler only the harness can reach, and the
// harness cannot reach it. So the whole decision — which key does what, in
// which context, and what happens when it cannot — is here, taking a key and a
// state and returning an intent. board-keys.test.ts exercises every row of
// §V14.4's table against it, and the component does nothing but obey.
//
// ---------------------------------------------------------------------------
// TWO ROWS OF THAT TABLE NAME MODULES THAT DO NOT EXIST YET
//
// `a` assigns the top-ranked candidate, which is V26's ranking. `o` sends an
// offer wave, which is V27's. Three ways to handle that, and two of them are
// wrong:
//
//   Leave the keys unbound — then §V14.4's table is two rows short of what
//   ships, silently, and the gap is invisible until somebody presses the key
//   and nothing happens. "Nothing happens" is the least debuggable outcome a
//   keyboard can produce.
//
//   Bind them to something approximate — assign the first candidate
//   alphabetically, offer to everyone selected — then the product has invented
//   a ranking, which is absolute rule 6's territory and V26's decision to make.
//
//   BIND THEM TO A NAMED REFUSAL. The key is bound, the press is acknowledged,
//   and the screen says which slice it is waiting for. That is a fact the user
//   can act on, and `unavailable` is a first-class intent below rather than a
//   null, so board-keys.test.ts asserts each key's reason by name.
// ---------------------------------------------------------------------------

import type { BoardFocus } from "./board";

/** What a key press means. Every member is something the screen can do. */
export type BoardIntent =
  | { kind: "move"; direction: "up" | "down" | "left" | "right" }
  /** `enter` — open what is under the cursor. */
  | { kind: "open" }
  /** `space` — select or deselect the focused cell. */
  | { kind: "select" }
  /** `r` — promote the focused reserve. */
  | { kind: "promote" }
  /** `/` — put the caret in the filter box. */
  | { kind: "focusFilter" }
  | { kind: "view"; view: BoardView }
  /** `esc` — clear the selection, then the filter. */
  | { kind: "clearSelection" }
  /**
   * The key is bound and the thing it does is not built.
   *
   * `slice` is carried so the screen names it. "Not available" with no reason
   * is indistinguishable from a bug, and this product's whole posture is that a
   * refusal states its cause.
   */
  | { kind: "unavailable"; action: string; slice: string; reason: string };

export type BoardView = "grid" | "timeline";

/**
 * What the board knows when a key arrives.
 *
 * DELIBERATELY SMALL. Anything richer than this and the decision starts
 * depending on the component's internals, at which point the unit test is
 * testing a mock rather than the product.
 */
export type BoardKeyContext = {
  focus: BoardFocus;
  /** Whether the focused cell holds a person at all. */
  focusedIsAssignment: boolean;
  /** Whether the focused person is on standby, so `r` means something. */
  focusedIsReserve: boolean;
  /** Whether the caret is already in a text field — then letters are letters. */
  typing: boolean;
};

/**
 * The keys, as data.
 *
 * §V14.4's table is ten rows; this is those ten rows and nothing else. It is
 * exported so the screen can render the shortcut legend FROM IT rather than
 * from a second list in JSX — a legend that can disagree with the handler is
 * worse than no legend, because it is trusted.
 */
export const BOARD_KEYS: { key: string; label: string; action: string }[] = [
  { key: "↑ ↓ ← →", label: "Arrow keys", action: "Move between places" },
  { key: "Enter", label: "Enter", action: "Open the place under the cursor" },
  { key: "Space", label: "Space", action: "Select or deselect this place" },
  { key: "a", label: "a", action: "Assign the top-ranked candidate" },
  { key: "r", label: "r", action: "Promote a reserve" },
  { key: "o", label: "o", action: "Send an offer wave to the selection" },
  { key: "/", label: "/", action: "Jump to the filter" },
  { key: "g", label: "g", action: "Grid view" },
  { key: "t", label: "t", action: "Timeline view" },
  { key: "Escape", label: "Esc", action: "Clear the selection" },
];

/**
 * A key press, resolved to an intent — or null if the board should ignore it.
 *
 * `typing` IS CHECKED FIRST FOR EVERY UNMODIFIED LETTER, and it is the bug this
 * shape exists to prevent: a single-letter shortcut on a screen with a filter
 * box means typing "grid" into the filter switches the view three times and
 * clears the selection. Escape and the arrows survive typing, because leaving a
 * field is the one thing a keyboard user must always be able to do.
 */
export function keyIntent(key: string, context: BoardKeyContext): BoardIntent | null {
  switch (key) {
    case "ArrowUp":
      return { kind: "move", direction: "up" };
    case "ArrowDown":
      return { kind: "move", direction: "down" };
    case "ArrowLeft":
      return { kind: "move", direction: "left" };
    case "ArrowRight":
      return { kind: "move", direction: "right" };
    case "Escape":
      return { kind: "clearSelection" };
    default:
      break;
  }

  if (context.typing) return null;

  switch (key) {
    case "Enter":
      // AN EMPTY PLACE HAS NOTHING TO OPEN UNTIL THERE IS A CANDIDATE LIST.
      // §V14.4 calls Enter "open the assignment picker", and a picker with
      // nobody in it is V26's ranking missing, not a screen that failed.
      return context.focusedIsAssignment
        ? { kind: "open" }
        : {
            kind: "unavailable",
            action: "Open the assignment picker",
            slice: "V26",
            reason: "Choosing somebody for an open place needs the candidate ranking, which arrives with auto-match.",
          };
    case " ":
    case "Spacebar":
      return { kind: "select" };
    case "a":
      return {
        kind: "unavailable",
        action: "Assign the top-ranked candidate",
        slice: "V26",
        reason: "There is no ranking yet. Auto-match builds it, and it has to be explainable in a sentence before it can rank anybody.",
      };
    case "r":
      return context.focusedIsReserve
        ? { kind: "promote" }
        : {
            kind: "unavailable",
            action: "Promote a reserve",
            slice: "",
            reason: "The place under the cursor is not somebody on standby.",
          };
    case "o":
      return {
        kind: "unavailable",
        action: "Send an offer wave",
        slice: "V27",
        reason: "Offer waves and their reserve rules arrive with the offer engine. Until then an offer is made one person at a time.",
      };
    case "/":
      return { kind: "focusFilter" };
    case "g":
      return { kind: "view", view: "grid" };
    case "t":
      return { kind: "view", view: "timeline" };
    default:
      return null;
  }
}

/**
 * The sentence the screen shows for a refused key.
 *
 * ASSEMBLED HERE so the wording cannot drift between the two renderers. A slice
 * number is appended only when there is one — `r` on a cell that holds no
 * reserve is not waiting for a slice, it is the user having pressed it in the
 * wrong place, and hanging a version number off that would be misleading.
 */
export function unavailableSentence(
  intent: Extract<BoardIntent, { kind: "unavailable" }>,
  /** S145: the screen's translateLabel, so the action and reason read in the reader's language. */
  say: (text: string) => string = (text) => text,
): string {
  const where = intent.slice === "" ? "" : ` (${intent.slice})`;
  return `${say(intent.action)}${where}: ${say(intent.reason)}`;
}
